import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PaymentStatusOverview = ({ 
  participants = [],
  totalBill = 0,
  currentUserId = null,
  onSendReminder = () => {},
  onCompleteSession = () => {},
  onExportAllReceipts = () => {}
}) => {
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const getDisplayName = (participant) => {
    return participant?.id === currentUserId ? 'Tú' : participant?.name;
  };

  const paidParticipants = participants?.filter(p => p?.paymentStatus === 'paid');
  const pendingParticipants = participants?.filter(p => p?.paymentStatus === 'unpaid');
  const processingParticipants = participants?.filter(p => p?.paymentStatus === 'processing');

  const totalPaid = paidParticipants?.reduce((sum, p) => sum + p?.totalAmount, 0);
  const totalPending = pendingParticipants?.reduce((sum, p) => sum + p?.totalAmount, 0);
  const totalProcessing = processingParticipants?.reduce((sum, p) => sum + p?.totalAmount, 0);

  const completionPercentage = participants?.length > 0 ? (paidParticipants?.length / participants?.length) * 100 : 0;
  const isSessionComplete = pendingParticipants?.length === 0 && processingParticipants?.length === 0;

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Estado de Pagos</h3>
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${isSessionComplete ? 'bg-success animate-pulse-soft' : 'bg-warning'}`} />
          <span className="text-sm text-text-secondary">
            {isSessionComplete ? 'Completado' : 'En progreso'}
          </span>
        </div>
      </div>
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-text-secondary">Progreso de pagos</span>
          <span className="font-medium text-foreground">{Math.round(completionPercentage)}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-success h-2 rounded-full transition-all duration-500"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>
      {/* Payment Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div className="text-center p-3 bg-success/5 border border-success/20 rounded-lg">
          <div className="flex items-center justify-center mb-1">
            <Icon name="CheckCircle" size={16} className="text-success mr-1" />
            <span className="text-sm font-medium text-success">Pagado</span>
          </div>
          <div className="font-mono font-semibold text-foreground">{formatAmount(totalPaid)}</div>
          <div className="text-xs text-text-secondary">{paidParticipants?.length} personas</div>
        </div>

        <div className="text-center p-3 bg-warning/5 border border-warning/20 rounded-lg">
          <div className="flex items-center justify-center mb-1">
            <Icon name="Clock" size={16} className="text-warning mr-1" />
            <span className="text-sm font-medium text-warning">Pendiente</span>
          </div>
          <div className="font-mono font-semibold text-foreground">{formatAmount(totalPending)}</div>
          <div className="text-xs text-text-secondary">{pendingParticipants?.length} personas</div>
        </div>

        <div className="text-center p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center justify-center mb-1">
            <Icon name="Loader" size={16} className="text-primary mr-1" />
            <span className="text-sm font-medium text-primary">Procesando</span>
          </div>
          <div className="font-mono font-semibold text-foreground">{formatAmount(totalProcessing)}</div>
          <div className="text-xs text-text-secondary">{processingParticipants?.length} personas</div>
        </div>
      </div>
      {/* Pending Participants */}
      {pendingParticipants?.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-foreground mb-2">Pagos pendientes:</h4>
          <div className="space-y-2">
            {pendingParticipants?.map((participant) => (
              <div key={participant?.id} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 rounded-full bg-warning text-warning-foreground flex items-center justify-center text-xs font-medium">
                    {participant?.id === currentUserId ? 'T' : participant?.name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2)}
                  </div>
                  <span className="text-sm text-foreground">{getDisplayName(participant)}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="font-mono text-sm text-foreground">{formatAmount(participant?.totalAmount)}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSendReminder(participant?.id)}
                    iconName="Bell"
                    iconSize={14}
                    className="h-6 w-6 p-0"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Total Summary */}
      <div className="border-t border-border pt-4 mb-4">
        <div className="flex justify-between items-center">
          <span className="font-medium text-foreground">Total de la cuenta:</span>
          <span className="font-mono font-semibold text-lg text-foreground">
            {formatAmount(totalBill)}
          </span>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="space-y-2">
        {isSessionComplete ? (
          <Button
            variant="success"
            fullWidth
            onClick={onCompleteSession}
            iconName="CheckCircle"
            iconPosition="left"
            iconSize={16}
          >
            Finalizar Sesión
          </Button>
        ) : (
          <Button
            variant="outline"
            fullWidth
            onClick={() => pendingParticipants?.forEach(p => onSendReminder(p?.id))}
            iconName="Bell"
            iconPosition="left"
            iconSize={16}
          >
            Enviar Recordatorio a Todos
          </Button>
        )}
        
        <Button
          variant="outline"
          fullWidth
          onClick={onExportAllReceipts}
          iconName="Download"
          iconPosition="left"
          iconSize={16}
        >
          Exportar Todos los Recibos
        </Button>
      </div>
    </div>
  );
};

export default PaymentStatusOverview;